import { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import wordcloud from 'highcharts/modules/wordcloud';
import treemap from 'highcharts/modules/treemap';
import heatmap from 'highcharts/modules/heatmap';
import exporting from 'highcharts/modules/exporting';
import exportData from 'highcharts/modules/export-data';
import accessibility from 'highcharts/modules/accessibility';
import { 
  Typography, Box, Grid, CircularProgress, Alert, Divider, Button
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

// Import the extracted components
import ChartContainer from './charts/ChartContainer';
import CategoryPieChart from './charts/CategoryPieChart';
import CategoryBarChart from './charts/CategoryBarChart';
import MultiCategoryChart from './charts/MultiCategoryChart';
import CoOccurrenceHeatmap from './charts/CoOccurrenceHeatmap';
import KeywordWordCloud from './charts/KeywordWordCloud';
import CategoryTreemap from './charts/CategoryTreemap';
import CategoryDonutChart from './charts/CategoryDonutChart';
import KeywordsByCategoryChart from './charts/KeywordsByCategoryChart';
import KeywordDataTable from './KeywordDataTable';
import { parseJSONData, convertJSONToCSV, ProcessedData, JSONRecord } from './utils/jsonDataProcessing';

// Initialize Highcharts modules
if (typeof Highcharts === 'object') {
  wordcloud(Highcharts);
  treemap(Highcharts);
  heatmap(Highcharts);
  exporting(Highcharts);
  exportData(Highcharts);
  accessibility(Highcharts);
}

// Define the props type
interface KeywordResultsVisualizationProps {
  jsonData: string | null;
  isLoading: boolean;
  isSavingToSnowflake?: boolean;
  snowflakeSaveSuccess?: boolean;
  snowflakeSaveError?: string | null;
}

const KeywordResultsVisualization = ({ 
  jsonData, 
  isLoading, 
  isSavingToSnowflake, 
  snowflakeSaveSuccess, 
  snowflakeSaveError 
}: KeywordResultsVisualizationProps) => {
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  
  // Parse JSON data when it's available
  useEffect(() => {
    if (!jsonData) return;
    
    try {
      const processed = parseJSONData(jsonData);
      setProcessedData(processed);
      setError(null);
    } catch (err) {
      console.error('Error parsing JSON data:', err);
      setError('Failed to parse the JSON data. The format may be invalid.');
      setProcessedData(null);
    }
  }, [jsonData]);

  const exportAllData = () => {
    if (!processedData) return;
    
    const csvContent = convertJSONToCSV(processedData.records);
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'keyword_classification_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!processedData) {
    return <Alert severity="info">No keyword classification data available.</Alert>;
  }

  const { categoryData, locationStats, intentStats, sourceStats, records } = processedData;

  return (
    <Box sx={{ mt: 4 }}>
      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h4">{records.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total Keywords</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h4">{locationStats.percentageWithLocation.toFixed(1)}%</Typography>
            <Typography variant="body2" color="text.secondary">With Location</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h4">{intentStats.averageCommercialScore.toFixed(0)}</Typography>
            <Typography variant="body2" color="text.secondary">Avg Commercial Score</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h4">{categoryData.categories.length}</Typography>
            <Typography variant="body2" color="text.secondary">Unique Categories</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Charts Grid Layout - 2 per row with improved spacing */}
      <Grid container spacing={8}>
        {/* Row 1: Category Distribution */}
        <Grid item xs={12} md={6}>
          <ChartContainer title="Categories by Percentage">
            <CategoryPieChart categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer title="Category Counts">
            <CategoryBarChart categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        {/* Row 2: Multi-category & Co-occurrence */}
        <Grid item xs={12} md={6}>
          <ChartContainer title="Keywords with Multiple Categories">
            <MultiCategoryChart categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer title="Co-occurrence of Categories">
            <CoOccurrenceHeatmap categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        {/* Row 3: Word Cloud & Treemap */}
        <Grid item xs={12} md={6}>
          <ChartContainer title="Top 20 Keywords by Frequency">
            <KeywordWordCloud categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer title="Category Size Treemap">
            <CategoryTreemap categoryData={categoryData} />
          </ChartContainer>
        </Grid>
        
        {/* Row 4: Donut Chart & Stacked Bar */}
        <Grid item xs={12} md={6}>
          <ChartContainer title="Category Donut Chart">
            <CategoryDonutChart categoryData={categoryData} />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer 
            title={showAllKeywords ? 'All Keywords by Category' : 'First 20 Keywords by Category'}
            actionButton={
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setShowAllKeywords(!showAllKeywords)}
              >
                {showAllKeywords ? 'Show Less' : 'Show All'}
              </Button>
            }
          >
            <KeywordsByCategoryChart 
              categoryData={categoryData} 
              parsedData={records} 
              showAllKeywords={showAllKeywords} 
            />
          </ChartContainer>
        </Grid>

        {/* Row 5: Location & Intent Stats - Full Width */}
        <Grid item xs={12}>
          <ChartContainer title="Location Distribution">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <Typography variant="subtitle2" gutterBottom>Top Cities</Typography>
                  {Object.entries(locationStats.cityDistribution)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 10)
                    .map(([city, count]) => (
                      <Box key={city} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{city}</Typography>
                        <Typography variant="body2" color="primary">{count}</Typography>
                      </Box>
                    ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <Typography variant="subtitle2" gutterBottom>Search Intent Distribution</Typography>
                  {Object.entries(intentStats.intentDistribution)
                    .sort(([,a], [,b]) => b - a)
                    .map(([intent, count]) => (
                      <Box key={intent} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{intent}</Typography>
                        <Typography variant="body2" color="primary">{count}</Typography>
                      </Box>
                    ))}
                </Box>
              </Grid>
            </Grid>
          </ChartContainer>
        </Grid>

        {/* Row 6: Source Module Distribution */}
        <Grid item xs={12}>
          <ChartContainer title="Classification Source Modules">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Topic Sources</Typography>
                  {Object.entries(sourceStats.topicSourceDistribution).map(([source, count]) => (
                    <Box key={source} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{source}</Typography>
                      <Typography variant="body2" color="primary">{count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Subtopic Sources</Typography>
                  {Object.entries(sourceStats.subtopicSourceDistribution).map(([source, count]) => (
                    <Box key={source} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{source}</Typography>
                      <Typography variant="body2" color="primary">{count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </ChartContainer>
        </Grid>

        {/* Row 7: Data Preview - Full Width */}
        <Grid item xs={12}>
          <ChartContainer 
            title={`Complete Keyword Data (${records.length} keywords total)`}
            actionButton={
              <Button 
                startIcon={<DownloadIcon />} 
                variant="contained" 
                color="primary" 
                onClick={exportAllData}
              >
                Export All Data
              </Button>
            }
          >
            <KeywordDataTable 
              parsedData={records} 
              categoryData={categoryData} 
            />
          </ChartContainer>
        </Grid>

        {/* Snowflake Save Status */}
        {(isSavingToSnowflake || snowflakeSaveSuccess || snowflakeSaveError) && (
          <Grid item xs={12}>
            <Box sx={{ pt: 2 }}>
              {isSavingToSnowflake && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Saving results to Snowflake...
                </Alert>
              )}
              {snowflakeSaveSuccess && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  Results successfully saved to Snowflake workflow results table
                </Alert>
              )}
              {snowflakeSaveError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Failed to save to Snowflake: {snowflakeSaveError}
                </Alert>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default KeywordResultsVisualization;
